/**
 * Partner-function labels for the whole ERP cluster (D2).
 *
 * ONE vocabulary for purchase orders, sales orders, their per-relationship
 * defaults and shipment parties alike: the tokens come from the generated
 * `party_functions` config enum, and the labels from the
 * `shipments:partyFunction.*` bundle that already translates every one of them —
 * so a party propagated from an order onto the transport reads the same word on
 * both screens.
 *
 * Which roles a document's HEADER owns is NOT here: it differs per owner
 * (SUPPLIER on a purchase order, BUYER on a sales order) and is passed to
 * `PartnerFunctionsCard` as `headerOwnedFunctions`. A single shared constant
 * would have to be the union of both, which would hide each owner's real roles
 * from the other's picker.
 */
import type { TFunction } from 'i18next';
import { PARTY_FUNCTION_VALUES, type PartyFunction } from '../../../generated/config-enums';

export type { PartyFunction };

export function partyFunctionLabel(t: TFunction, token: string): string {
  return t(`shipments:partyFunction.${token.toLowerCase()}`, { defaultValue: token });
}

/** The 39 functions sorted by their translated label — the config order groups by domain, which a picker cannot show. */
export function partyFunctionOptions(t: TFunction): Array<{ value: PartyFunction; label: string }> {
  return PARTY_FUNCTION_VALUES.map((value) => ({ value, label: partyFunctionLabel(t, value) })).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

/**
 * The functions an owner may ADD as a stored party row.
 *
 * The whole vocabulary minus the roles that owner's header owns — SUPPLIER on a
 * purchase order, BUYER on a sales order, plus whatever this particular document's
 * header has actually filled in. Offering one of those would let the add dialog
 * silently re-point `vendor_id` / `customer_id` behind the header's own
 * editability rules, so the exclusion is the load-bearing half of the picker.
 *
 * Named and exported rather than inlined in the card so it can be asserted
 * directly: the picker is a Radix Select inside a Radix Dialog, two nested portals
 * that jsdom drives unreliably, and a rule this consequential should not be
 * reachable only through them.
 */
export function selectablePartyFunctionOptions(
  t: TFunction,
  headerOwned: ReadonlySet<string>,
): Array<{ value: PartyFunction; label: string }> {
  return partyFunctionOptions(t).filter((option) => !headerOwned.has(option.value));
}
